import { buildParentMap } from "@cva/shared";
import { useQueryClient } from "@tanstack/react-query";
import {
  ArrowDownToLine,
  ArrowUpToLine,
  BookmarkPlus,
  ChevronDown,
  ChevronUp,
  ClipboardPaste,
  Copy,
  CopyPlus,
  Group,
  LayoutGrid,
  Lock,
  LockOpen,
  Smartphone,
  Trash2,
  Ungroup,
  Wand2,
} from "lucide-react";
import type { ReactNode } from "react";
import {
  DropdownContent,
  DropdownItem,
  DropdownMenu,
  DropdownSeparator,
  DropdownTrigger,
} from "../components/ui/dropdown";
import {
  copySelection,
  deleteSelection,
  duplicateSelection,
  groupSelection,
  pasteData,
  reorderSelection,
  toggleLock,
  ungroupSelection,
} from "./commands";
import { startCutout } from "./cutout";
import { useEnv } from "./env";
import { imageToDevice } from "./mockup-commands";
import { requestSaveBlock } from "./SaveBlockDialog";
import { StyleTransferItems } from "./StyleTransferItems";
import { useEditor, useEditorApi } from "./store";
import { canTidy, tidySelection } from "./tidy";
import { BehindPersonItem } from "./video/PersonCutoutSection";

function Shortcut({ children }: { children: ReactNode }) {
  return <span className="ml-auto pl-6 text-xs text-zinc-400">{children}</span>;
}

/** Can the selection be grouped (two or more elements with the same parent)? */
export function canGroup(
  doc: Parameters<typeof buildParentMap>[0],
  selection: readonly string[],
): boolean {
  if (selection.length < 2) return false;
  const parents = buildParentMap(doc);
  const first = parents.get(selection[0]!);
  return selection.every((id) => {
    const parent = parents.get(id);
    if (!first || !parent || parent.kind !== first.kind) return false;
    return parent.kind === "page"
      ? first.kind === "page" && parent.pageIndex === first.pageIndex
      : first.kind === "group" && parent.groupId === first.groupId;
  });
}

/**
 * Right-click menu of the canvas: grouping, saving as a building block,
 * copy & paste, order, lock and delete – the actions of the properties panel
 * where the pointer is.
 */
export function CanvasContextMenu({
  at,
  onClose,
}: {
  at: { x: number; y: number } | null;
  onClose: () => void;
}) {
  const store = useEditorApi();
  const env = useEnv();
  const queryClient = useQueryClient();
  const selection = useEditor((s) => s.selection);
  const doc = useEditor((s) => s.doc);
  const readOnly = useEditor((s) => s.readOnly);
  if (!at || readOnly) return null;
  const elements = selection.flatMap((id) => (doc.elements[id] ? [doc.elements[id]!] : []));
  const only = elements.length === 1 ? elements[0] : undefined;
  const image = only?.type === "image" && !only.locked ? only : null;
  const imageAsset = image ? env.assets.get(image.assetId) : undefined;
  const canCutOut =
    image &&
    imageAsset?.kind === "image" &&
    !imageAsset.cutout &&
    imageAsset.status !== "processing";
  const hasGroup = elements.some((element) => element.type === "group");
  const locked = elements.length > 0 && elements.every((element) => element.locked);
  const saveable = elements.some((element) => element.type !== "captions");
  const run = (action: () => void) => () => {
    action();
    onClose();
  };
  return (
    <DropdownMenu open onOpenChange={(open) => !open && onClose()} modal={false}>
      <DropdownTrigger asChild>
        <span
          aria-hidden="true"
          style={{ position: "fixed", left: at.x, top: at.y, width: 1, height: 1 }}
        />
      </DropdownTrigger>
      <DropdownContent align="start" className="min-w-56">
        {elements.length === 0 ? (
          <>
            <DropdownItem onSelect={run(() => pasteData(store))}>
              <ClipboardPaste /> Einfügen <Shortcut>⌘V</Shortcut>
            </DropdownItem>
            <DropdownItem
              onSelect={run(() =>
                store.getState().select(doc.pages[store.getState().pageIndex]?.children ?? []),
              )}
            >
              <CopyPlus /> Alles auswählen <Shortcut>⌘A</Shortcut>
            </DropdownItem>
          </>
        ) : (
          <>
            {canGroup(doc, selection) ? (
              <DropdownItem onSelect={run(() => groupSelection(store))}>
                <Group /> Gruppieren <Shortcut>⌘G</Shortcut>
              </DropdownItem>
            ) : null}
            {canTidy(store) ? (
              <DropdownItem onSelect={run(() => tidySelection(store))}>
                <LayoutGrid /> Aufräumen
              </DropdownItem>
            ) : null}
            {hasGroup ? (
              <DropdownItem onSelect={run(() => ungroupSelection(store))}>
                <Ungroup /> Gruppierung aufheben <Shortcut>⇧⌘G</Shortcut>
              </DropdownItem>
            ) : null}
            {saveable ? (
              <DropdownItem onSelect={run(() => requestSaveBlock(selection))}>
                <BookmarkPlus /> Als Baustein speichern …
              </DropdownItem>
            ) : null}
            <BehindPersonItem ids={selection} onDone={onClose} />
            {canCutOut ? (
              <DropdownItem
                onSelect={run(
                  () =>
                    void startCutout({
                      orgId: env.orgId,
                      store,
                      queryClient,
                      target: { kind: "element", id: image.id },
                      source: imageAsset,
                    }),
                )}
              >
                <Wand2 /> Hintergrund entfernen
              </DropdownItem>
            ) : null}
            {image ? (
              <DropdownItem
                onSelect={run(() =>
                  imageToDevice(store, image.id, {
                    id: image.assetId,
                    width: imageAsset?.width ?? null,
                    height: imageAsset?.height ?? null,
                  }),
                )}
              >
                <Smartphone /> Als Mockup zeigen
              </DropdownItem>
            ) : null}
            <DropdownSeparator />
            <DropdownItem onSelect={run(() => duplicateSelection(store))}>
              <CopyPlus /> Duplizieren <Shortcut>⌘D</Shortcut>
            </DropdownItem>
            <DropdownItem
              onSelect={run(() => {
                const text = copySelection(store);
                if (text) void navigator.clipboard?.writeText(text).catch(() => undefined);
              })}
            >
              <Copy /> Kopieren <Shortcut>⌘C</Shortcut>
            </DropdownItem>
            <DropdownItem onSelect={run(() => pasteData(store))}>
              <ClipboardPaste /> Einfügen <Shortcut>⌘V</Shortcut>
            </DropdownItem>
            <StyleTransferItems run={run} />
            <DropdownSeparator />
            <DropdownItem onSelect={run(() => reorderSelection(store, "front"))}>
              <ArrowUpToLine /> Ganz nach vorne <Shortcut>⇧⌘]</Shortcut>
            </DropdownItem>
            <DropdownItem onSelect={run(() => reorderSelection(store, "forward"))}>
              <ChevronUp /> Eine Ebene nach vorne <Shortcut>⌘]</Shortcut>
            </DropdownItem>
            <DropdownItem onSelect={run(() => reorderSelection(store, "backward"))}>
              <ChevronDown /> Eine Ebene nach hinten <Shortcut>⌘[</Shortcut>
            </DropdownItem>
            <DropdownItem onSelect={run(() => reorderSelection(store, "back"))}>
              <ArrowDownToLine /> Ganz nach hinten <Shortcut>⇧⌘[</Shortcut>
            </DropdownItem>
            <DropdownSeparator />
            <DropdownItem onSelect={run(() => toggleLock(store))}>
              {locked ? <LockOpen /> : <Lock />} {locked ? "Entsperren" : "Sperren"}
              <Shortcut>⌘L</Shortcut>
            </DropdownItem>
            <DropdownItem danger onSelect={run(() => deleteSelection(store))}>
              <Trash2 /> Löschen <Shortcut>⌫</Shortcut>
            </DropdownItem>
          </>
        )}
      </DropdownContent>
    </DropdownMenu>
  );
}
